import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Treemap
} from 'recharts';
import {
  Package, TrendingUp, DollarSign, RefreshCw, Download, Calendar, Wrench, User,
  FileText, Filter, Plus, X, Save, FolderOpen, Trash2, Settings, Table2,
  BarChart3, PieChartIcon, FileSpreadsheet, ChevronDown, ChevronRight, Layers,
  ArrowUpDown, Eye, EyeOff, Grip, Building2, MapPin, Tag, Truck, Clock, Hash
} from 'lucide-react';

const COLORS = ['#0052CC', '#FF5630', '#36B37E', '#6554C0', '#FF8B00', '#00B8D9', '#8777D9', '#57D9A3', '#FFC400', '#FF7452', '#253858', '#00875A'];

const FILTER_ICONS = {
  plant_id: Building2,
  location_id: MapPin,
  equipment_id: Wrench,
  technician_id: User,
  category_id: Tag,
  supplier_id: Truck,
  article_id: Package,
  month: Calendar,
  week: Clock
};

const AdvancedConsumptionReportPage = () => {
  // Data state
  const [filterOptions, setFilterOptions] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const [priceAnalysis, setPriceAnalysis] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);

  // Filter state
  const [activeFilters, setActiveFilters] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Grouping state
  const [groupBy, setGroupBy] = useState('');
  const [pivotRows, setPivotRows] = useState(['equipment_name']);
  const [pivotCols, setPivotCols] = useState('month');
  const [metrics, setMetrics] = useState(['quantity', 'value']);
  const [sortBy, setSortBy] = useState('total_value');
  const [sortOrder, setSortOrder] = useState('desc');

  // UI state
  const [activeTab, setActiveTab] = useState('table');
  const [showFiltersPanel, setShowFiltersPanel] = useState(true);
  const [showAddFilter, setShowAddFilter] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewMode, setViewMode] = useState('detailed'); // detailed, grouped, pivot
  const [expandedGroups, setExpandedGroups] = useState({});

  // New filter being added
  const [newFilterField, setNewFilterField] = useState('');
  const [newFilterValue, setNewFilterValue] = useState('');

  // Load filter options and templates
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [optionsRes, templatesRes] = await Promise.all([
          api.get('/reports/advanced-consumption/filters/options'),
          api.get('/reports/advanced-consumption/templates')
        ]);
        setFilterOptions(optionsRes.data);
        setTemplates(templatesRes.data);
      } catch (err) {
        toast.error('Eroare la incarcarea optiunilor');
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Execute report
  const executeReport = useCallback(async () => {
    try {
      setExecuting(true);
      
      // Build query params from active filters
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        group_by: groupBy || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: 1,
        page_size: 500
      };

      // Add active filters
      activeFilters.forEach(f => {
        params[f.field] = f.value;
      });

      const res = await api.get('/reports/advanced-consumption/data', { params });
      setReportData(res.data);
      
      // If pivot mode, also fetch pivot data
      if (viewMode === 'pivot') {
        const pivotRes = await api.post('/reports/advanced-consumption/pivot', {
          date_from: dateFrom,
          date_to: dateTo,
          group_by: pivotRows,
          columns_by: pivotCols,
          metrics: metrics,
          sort_by: sortBy,
          sort_order: sortOrder,
          filters: activeFilters.map(f => ({
            field: f.field,
            operator: 'eq',
            value: f.value
          }))
        });
        setPivotData(pivotRes.data);
      }

      toast.success(`Raport generat: ${res.data.summary?.total_records || 0} inregistrari`);
    } catch (err) {
      toast.error('Eroare la generarea raportului');
      console.error(err);
    } finally {
      setExecuting(false);
    }
  }, [dateFrom, dateTo, groupBy, sortBy, sortOrder, activeFilters, viewMode, pivotRows, pivotCols, metrics]);

  // Add filter
  const addFilter = () => {
    if (!newFilterField || !newFilterValue) return;
    
    // Get display name
    let displayName = newFilterValue;
    if (filterOptions) {
      const options = getFilterValueOptions(newFilterField);
      const option = options.find(o => o.id === newFilterValue || o.value === newFilterValue);
      if (option) displayName = option.name || option.label;
    }

    setActiveFilters(prev => [...prev, {
      id: `${newFilterField}-${Date.now()}`,
      field: newFilterField,
      value: newFilterValue,
      displayName
    }]);
    setNewFilterField('');
    setNewFilterValue('');
    setShowAddFilter(false);
  };

  // Remove filter
  const removeFilter = (filterId) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
  };

  // Clear all filters
  const clearFilters = () => {
    setActiveFilters([]);
    const d = new Date();
    d.setDate(d.getDate() - 90);
    setDateFrom(d.toISOString().split('T')[0]);
    setDateTo(new Date().toISOString().split('T')[0]);
  };

  // Get filter value options based on field
  const getFilterValueOptions = (field) => {
    if (!filterOptions) return [];
    switch (field) {
      case 'plant_id': return filterOptions.plants || [];
      case 'location_id': return filterOptions.locations || [];
      case 'equipment_id': return filterOptions.equipment || [];
      case 'technician_id': return filterOptions.technicians?.map(t => ({ id: t.id, name: t.full_name })) || [];
      case 'category_id': return filterOptions.categories || [];
      case 'supplier_id': return filterOptions.suppliers || [];
      case 'article_id': return filterOptions.articles || [];
      default: return [];
    }
  };

  // Get field label
  const getFieldLabel = (field) => {
    const labels = {
      plant_id: 'Fabrica',
      location_id: 'Locatie',
      equipment_id: 'Echipament',
      technician_id: 'Tehnician',
      category_id: 'Categorie',
      supplier_id: 'Furnizor',
      article_id: 'Articol'
    };
    return labels[field] || field;
  };

  // Save template
  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      await api.post('/reports/advanced-consumption/templates', {
        name: templateName,
        config: {
          filters: activeFilters.map(f => ({ field: f.field, operator: 'eq', value: f.value })),
          group_by: pivotRows,
          columns_by: pivotCols,
          metrics: metrics,
          sort_by: sortBy,
          sort_order: sortOrder,
          date_from: dateFrom,
          date_to: dateTo
        }
      });
      toast.success('Template salvat');
      setShowSaveTemplate(false);
      setTemplateName('');
      // Reload templates
      const res = await api.get('/reports/advanced-consumption/templates');
      setTemplates(res.data);
    } catch (err) {
      toast.error('Eroare la salvare');
    }
  };

  // Load template
  const loadTemplate = (template) => {
    const config = template.config;
    if (config.filters) {
      setActiveFilters(config.filters.map((f, i) => ({
        id: `${f.field}-${i}`,
        field: f.field,
        value: f.value,
        displayName: f.value
      })));
    }
    if (config.group_by) setPivotRows(config.group_by);
    if (config.columns_by) setPivotCols(config.columns_by);
    if (config.metrics) setMetrics(config.metrics);
    if (config.sort_by) setSortBy(config.sort_by);
    if (config.sort_order) setSortOrder(config.sort_order);
    setSelectedTemplate(template);
    toast.success(`Template "${template.name}" incarcat`);
  };

  // Delete template
  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/reports/advanced-consumption/templates/${id}`);
      toast.success('Template sters');
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      toast.error('Eroare la stergere');
    }
  };

  // Seed default templates
  const seedDefaultTemplates = async () => {
    try {
      await api.post('/reports/advanced-consumption/templates/seed-defaults');
      const res = await api.get('/reports/advanced-consumption/templates');
      setTemplates(res.data);
      toast.success('Template-uri default create');
    } catch (err) {
      toast.error('Eroare la creare template-uri');
    }
  };

  // Export to CSV
  const exportCSV = () => {
    if (!reportData) return;
    
    let rows = [];
    let headers = [];
    
    if (reportData.type === 'grouped') {
      headers = ['Grup', 'Cantitate', 'Valoare (RON)', 'Inregistrari', 'Articole Unice', 'Tichete Unice'];
      rows = reportData.data.map(g => [
        g.group_name,
        g.total_quantity,
        g.total_value,
        g.record_count,
        g.unique_articles,
        g.unique_tickets
      ]);
    } else {
      headers = ['Data', 'Tichet', 'Echipament', 'Locatie', 'Tehnician', 'Articol', 'Cod', 'Categorie', 'Cantitate', 'UM', 'Pret', 'Valoare', 'Lot', 'Furnizor'];
      rows = reportData.data.map(r => [
        r.closed_date,
        r.ticket_title,
        r.equipment_name,
        r.location_name,
        r.technician_name,
        r.article_name,
        r.article_code,
        r.category_name,
        r.quantity,
        r.unit,
        r.unit_price,
        r.total_value,
        r.lot_number,
        r.supplier_name
      ]);
    }
    
    const csv = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${c || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `raport_consum_${dateFrom}_${dateTo}.csv`;
    link.click();
    toast.success('Export CSV generat');
  };

  // Chart data
  const chartData = useMemo(() => {
    if (!reportData) return { pie: [], bar: [], line: [] };
    
    if (reportData.type === 'grouped') {
      return {
        pie: reportData.data.slice(0, 8).map(g => ({
          name: g.group_name?.length > 20 ? g.group_name.substring(0, 20) + '...' : g.group_name,
          value: Math.round(g.total_value)
        })),
        bar: reportData.data.slice(0, 15).map(g => ({
          name: g.group_name?.length > 15 ? g.group_name.substring(0, 15) + '...' : g.group_name,
          cantitate: Math.round(g.total_quantity),
          valoare: Math.round(g.total_value)
        }))
      };
    }
    
    // For detailed, group by date
    const byDate = {};
    reportData.data.forEach(r => {
      const date = r.closed_date || 'N/A';
      if (!byDate[date]) byDate[date] = { date, cantitate: 0, valoare: 0 };
      byDate[date].cantitate += r.quantity;
      byDate[date].valoare += r.total_value;
    });
    
    const lineData = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    
    // Group by article for pie
    const byArticle = {};
    reportData.data.forEach(r => {
      const key = r.article_name || 'N/A';
      if (!byArticle[key]) byArticle[key] = 0;
      byArticle[key] += r.total_value;
    });
    
    const pieData = Object.entries(byArticle)
      .map(([name, value]) => ({ name: name.length > 20 ? name.substring(0, 20) + '...' : name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    
    return { pie: pieData, bar: lineData.slice(-20), line: lineData };
  }, [reportData]);

  // Pivot chart data
  const pivotChartData = useMemo(() => {
    if (!pivotData?.pivot?.rows) return [];
    return pivotData.pivot.rows.slice(0, 15).map(row => {
      const entry = { name: Object.values(row.row_values).join(' | ').substring(0, 25) };
      if (row.columns && pivotData.pivot.column_values) {
        pivotData.pivot.column_values.forEach(col => {
          entry[col] = row.columns[col]?.value || 0;
        });
      }
      entry.total = row.totals?.value || 0;
      return entry;
    });
  }, [pivotData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="advanced-consumption-report-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Raport Avansat Consum Consumabile</h1>
          <p className="text-slate-500 text-sm mt-1">Analiza completa cu preturi din facturi, pivot tables si export</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowFiltersPanel(!showFiltersPanel)}>
            <Filter className="w-4 h-4 mr-1" />
            Filtre {activeFilters.length > 0 && <Badge className="ml-1 bg-blue-100 text-blue-700">{activeFilters.length}</Badge>}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate(true)} disabled={!reportData}>
            <Save className="w-4 h-4 mr-1" /> Salveaza
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!reportData}>
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button size="sm" onClick={executeReport} disabled={executing}>
            {executing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1" />}
            Genereaza
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left Sidebar - Filters & Config */}
        {showFiltersPanel && (
          <div className="col-span-12 lg:col-span-3 space-y-4">
            {/* Date Range */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4" /> Perioada</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">De la</Label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-sm" data-testid="date-from" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Pana la</Label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-sm" data-testid="date-to" />
                </div>
                <div className="flex gap-1 flex-wrap">
                  {[
                    { label: '7Z', days: 7 },
                    { label: '30Z', days: 30 },
                    { label: '90Z', days: 90 },
                    { label: '1A', days: 365 }
                  ].map(p => (
                    <Button key={p.label} variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() - p.days);
                      setDateFrom(d.toISOString().split('T')[0]);
                      setDateTo(new Date().toISOString().split('T')[0]);
                    }}>{p.label}</Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Active Filters */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Filter className="w-4 h-4" /> Filtre Active</CardTitle>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowAddFilter(true)}>
                    <Plus className="w-3 h-3 mr-1" /> Adauga
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {activeFilters.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">Niciun filtru activ</p>
                ) : (
                  <div className="space-y-2">
                    {activeFilters.map(filter => {
                      const Icon = FILTER_ICONS[filter.field] || Filter;
                      return (
                        <div key={filter.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg group">
                          <Icon className="w-3.5 h-3.5 text-slate-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-700">{getFieldLabel(filter.field)}</p>
                            <p className="text-xs text-slate-500 truncate">{filter.displayName}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100" onClick={() => removeFilter(filter.id)}>
                            <X className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      );
                    })}
                    <Button variant="ghost" size="sm" className="w-full text-xs text-slate-500" onClick={clearFilters}>
                      Sterge toate filtrele
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* View Mode & Grouping */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Mod Vizualizare</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger className="h-8 text-sm" data-testid="view-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="detailed">Detaliat (randuri)</SelectItem>
                    <SelectItem value="grouped">Grupat</SelectItem>
                    <SelectItem value="pivot">Pivot Table</SelectItem>
                  </SelectContent>
                </Select>

                {viewMode === 'grouped' && (
                  <div>
                    <Label className="text-xs text-slate-500">Grupeaza dupa</Label>
                    <Select value={groupBy} onValueChange={setGroupBy}>
                      <SelectTrigger className="h-8 text-sm" data-testid="group-by-select">
                        <SelectValue placeholder="Selecteaza" />
                      </SelectTrigger>
                      <SelectContent>
                        {filterOptions?.group_by_options?.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {viewMode === 'pivot' && (
                  <>
                    <div>
                      <Label className="text-xs text-slate-500">Randuri (grupare)</Label>
                      <Select value={pivotRows[0]} onValueChange={v => setPivotRows([v])}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equipment_name">Echipament</SelectItem>
                          <SelectItem value="technician_name">Tehnician</SelectItem>
                          <SelectItem value="article_name">Articol</SelectItem>
                          <SelectItem value="category_name">Categorie</SelectItem>
                          <SelectItem value="plant_name">Fabrica</SelectItem>
                          <SelectItem value="location_name">Locatie</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Coloane</Label>
                      <Select value={pivotCols} onValueChange={setPivotCols}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="month">Luna</SelectItem>
                          <SelectItem value="week">Saptamana</SelectItem>
                          <SelectItem value="category_name">Categorie</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <div>
                  <Label className="text-xs text-slate-500">Sorteaza dupa</Label>
                  <div className="flex gap-1">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-8 text-sm flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="total_value">Valoare</SelectItem>
                        <SelectItem value="total_quantity">Cantitate</SelectItem>
                        <SelectItem value="record_count">Nr. inregistrari</SelectItem>
                        <SelectItem value="closed_at">Data</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}>
                      <ArrowUpDown className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Templates */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Template-uri</CardTitle>
                  {templates.length === 0 && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={seedDefaultTemplates}>
                      <Plus className="w-3 h-3 mr-1" /> Default
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">Niciun template salvat</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {templates.map(tpl => (
                      <div 
                        key={tpl.id} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${selectedTemplate?.id === tpl.id ? 'bg-blue-50 border border-blue-200' : ''}`}
                        onClick={() => loadTemplate(tpl)}
                      >
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{tpl.name}</p>
                          {tpl.is_default && <Badge className="text-[10px] bg-green-100 text-green-700">Default</Badge>}
                        </div>
                        {!tpl.is_default && (
                          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl.id); }}>
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <div className={`col-span-12 ${showFiltersPanel ? 'lg:col-span-9' : ''} space-y-4`}>
          {/* Summary KPIs */}
          {reportData?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Card className="bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-3 text-center">
                  <Hash className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                  <p className="text-xl font-bold text-slate-900">{reportData.summary.total_records?.toLocaleString('ro-RO')}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Inregistrari</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-3 text-center">
                  <Package className="w-5 h-5 mx-auto text-purple-500 mb-1" />
                  <p className="text-xl font-bold text-slate-900">{reportData.summary.total_quantity?.toLocaleString('ro-RO')}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Cantitate</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-3 text-center">
                  <DollarSign className="w-5 h-5 mx-auto text-green-500 mb-1" />
                  <p className="text-xl font-bold text-slate-900">{reportData.summary.total_value?.toLocaleString('ro-RO', { maximumFractionDigits: 0 })}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Valoare RON</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-50 to-white">
                <CardContent className="p-3 text-center">
                  <Tag className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                  <p className="text-xl font-bold text-slate-900">{reportData.summary.unique_articles}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Articole</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-cyan-50 to-white">
                <CardContent className="p-3 text-center">
                  <FileText className="w-5 h-5 mx-auto text-cyan-500 mb-1" />
                  <p className="text-xl font-bold text-slate-900">{reportData.summary.unique_tickets}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Tichete</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-rose-50 to-white">
                <CardContent className="p-3 text-center">
                  <Wrench className="w-5 h-5 mx-auto text-rose-500 mb-1" />
                  <p className="text-xl font-bold text-slate-900">{reportData.summary.unique_equipment}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Echipamente</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabs: Table / Charts / Pivot */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full max-w-md grid-cols-4">
              <TabsTrigger value="table" data-testid="tab-table"><Table2 className="w-4 h-4 mr-1" /> Tabel</TabsTrigger>
              <TabsTrigger value="charts" data-testid="tab-charts"><BarChart3 className="w-4 h-4 mr-1" /> Grafice</TabsTrigger>
              <TabsTrigger value="pivot" data-testid="tab-pivot"><Layers className="w-4 h-4 mr-1" /> Pivot</TabsTrigger>
              <TabsTrigger value="prices" data-testid="tab-prices"><DollarSign className="w-4 h-4 mr-1" /> Preturi</TabsTrigger>
            </TabsList>

            {/* Table Tab */}
            <TabsContent value="table" className="mt-4">
              {!reportData ? (
                <Card className="h-96">
                  <CardContent className="flex flex-col items-center justify-center h-full text-slate-400">
                    <BarChart3 className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">Configurati filtrele si apasati "Genereaza"</p>
                  </CardContent>
                </Card>
              ) : reportData.type === 'grouped' ? (
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 z-10">
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-semibold text-slate-700">Grup</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Cantitate</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Valoare (RON)</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Inregistrari</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Articole</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Tichete</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-700">Medie/Inreg.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.data.map((row, i) => (
                            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4 font-medium text-slate-900">{row.group_name}</td>
                              <td className="py-3 px-4 text-right">{row.total_quantity.toLocaleString('ro-RO')}</td>
                              <td className="py-3 px-4 text-right font-semibold text-green-700">{row.total_value.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                              <td className="py-3 px-4 text-right">{row.record_count}</td>
                              <td className="py-3 px-4 text-right">{row.unique_articles}</td>
                              <td className="py-3 px-4 text-right">{row.unique_tickets}</td>
                              <td className="py-3 px-4 text-right text-slate-500">{row.avg_value_per_record?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-100 font-bold">
                          <tr>
                            <td className="py-3 px-4">TOTAL</td>
                            <td className="py-3 px-4 text-right">{reportData.summary.total_quantity?.toLocaleString('ro-RO')}</td>
                            <td className="py-3 px-4 text-right text-green-700">{reportData.summary.total_value?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                            <td className="py-3 px-4 text-right">{reportData.summary.total_records}</td>
                            <td className="py-3 px-4 text-right">{reportData.summary.unique_articles}</td>
                            <td className="py-3 px-4 text-right">{reportData.summary.unique_tickets}</td>
                            <td className="py-3 px-4 text-right">-</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 z-10">
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 font-semibold text-slate-700 text-xs">#</th>
                            <th className="text-left py-2 px-3 font-semibold text-slate-700 text-xs">Data</th>
                            <th className="text-left py-2 px-3 font-semibold text-slate-700 text-xs">Tichet</th>
                            <th className="text-left py-2 px-3 font-semibold text-slate-700 text-xs">Echipament</th>
                            <th className="text-left py-2 px-3 font-semibold text-slate-700 text-xs">Articol</th>
                            <th className="text-right py-2 px-3 font-semibold text-slate-700 text-xs">Cant.</th>
                            <th className="text-right py-2 px-3 font-semibold text-slate-700 text-xs">Pret</th>
                            <th className="text-right py-2 px-3 font-semibold text-slate-700 text-xs">Valoare</th>
                            <th className="text-left py-2 px-3 font-semibold text-slate-700 text-xs">Furnizor</th>
                            <th className="text-center py-2 px-3 font-semibold text-slate-700 text-xs">Factura</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.data.map((row, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="py-2 px-3 text-xs text-slate-400">{i + 1}</td>
                              <td className="py-2 px-3 text-xs">{row.closed_date}</td>
                              <td className="py-2 px-3 text-xs max-w-[150px] truncate" title={row.ticket_title}>{row.ticket_title}</td>
                              <td className="py-2 px-3 text-xs max-w-[120px] truncate" title={row.equipment_name}>{row.equipment_name}</td>
                              <td className="py-2 px-3 text-xs font-medium max-w-[150px] truncate" title={row.article_name}>{row.article_name}</td>
                              <td className="py-2 px-3 text-xs text-right">{row.quantity} {row.unit}</td>
                              <td className="py-2 px-3 text-xs text-right">{row.unit_price > 0 ? row.unit_price.toLocaleString('ro-RO', { minimumFractionDigits: 2 }) : '-'}</td>
                              <td className="py-2 px-3 text-xs text-right font-semibold text-green-700">{row.total_value.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                              <td className="py-2 px-3 text-xs max-w-[100px] truncate" title={row.supplier_name}>{row.supplier_name || '-'}</td>
                              <td className="py-2 px-3 text-center">
                                {row.has_invoice_price ? (
                                  <Badge className="text-[9px] bg-green-100 text-green-700">DA</Badge>
                                ) : (
                                  <Badge className="text-[9px] bg-slate-100 text-slate-500">NU</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Charts Tab */}
            <TabsContent value="charts" className="mt-4 space-y-4">
              {!reportData ? (
                <Card className="h-96">
                  <CardContent className="flex flex-col items-center justify-center h-full text-slate-400">
                    <PieChartIcon className="w-16 h-16 mb-4 opacity-50" />
                    <p>Generati raportul pentru a vedea graficele</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Pie Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Distributie Valoare</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={chartData.pie} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                            {chartData.pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(val) => val.toLocaleString('ro-RO') + ' RON'} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Bar Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Top Consumuri</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={chartData.bar} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 9 }} />
                          <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} />
                          <Legend />
                          <Bar dataKey="valoare" name="Valoare" fill="#0052CC" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Line/Area Chart - Trend */}
                  <Card className="md:col-span-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Evolutie in Timp</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={chartData.line}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} />
                          <Legend />
                          <Area type="monotone" dataKey="valoare" name="Valoare (RON)" stroke="#0052CC" fill="#0052CC" fillOpacity={0.1} />
                          <Area type="monotone" dataKey="cantitate" name="Cantitate" stroke="#36B37E" fill="#36B37E" fillOpacity={0.1} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Pivot Tab */}
            <TabsContent value="pivot" className="mt-4">
              {!pivotData ? (
                <Card className="h-96">
                  <CardContent className="flex flex-col items-center justify-center h-full text-slate-400">
                    <Layers className="w-16 h-16 mb-4 opacity-50" />
                    <p>Selectati modul Pivot si generati raportul</p>
                    <Button className="mt-4" onClick={() => { setViewMode('pivot'); executeReport(); }}>
                      Genereaza Pivot
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Pivot Table */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Pivot: {pivotRows.join(', ')} x {pivotCols}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-100 z-10">
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 font-semibold text-slate-700">{pivotRows.join(' / ')}</th>
                              {pivotData.pivot.column_values?.map(col => (
                                <th key={col} className="text-right py-2 px-3 font-semibold text-slate-700 min-w-[80px]">{col}</th>
                              ))}
                              <th className="text-right py-2 px-3 font-bold text-blue-700 bg-blue-50">TOTAL</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pivotData.pivot.rows?.map((row, i) => (
                              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                                <td className="py-2 px-3 font-medium">{Object.values(row.row_values).join(' | ')}</td>
                                {pivotData.pivot.column_values?.map(col => (
                                  <td key={col} className="py-2 px-3 text-right">
                                    {row.columns?.[col]?.value?.toLocaleString('ro-RO', { minimumFractionDigits: 0 }) || '-'}
                                  </td>
                                ))}
                                <td className="py-2 px-3 text-right font-bold text-blue-700 bg-blue-50">
                                  {row.totals?.value?.toLocaleString('ro-RO', { minimumFractionDigits: 0 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-100 font-bold">
                            <tr>
                              <td className="py-2 px-3">TOTAL GENERAL</td>
                              {pivotData.pivot.column_values?.map(col => {
                                const total = pivotData.pivot.rows?.reduce((sum, row) => sum + (row.columns?.[col]?.value || 0), 0);
                                return <td key={col} className="py-2 px-3 text-right">{total?.toLocaleString('ro-RO', { minimumFractionDigits: 0 })}</td>;
                              })}
                              <td className="py-2 px-3 text-right text-blue-700 bg-blue-50">
                                {pivotData.grand_totals?.value?.toLocaleString('ro-RO', { minimumFractionDigits: 0 })}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pivot Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Vizualizare Pivot</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={pivotChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={60} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} />
                          <Legend />
                          {pivotData.pivot.column_values?.slice(0, 6).map((col, i) => (
                            <Bar key={col} dataKey={col} stackId="a" fill={COLORS[i % COLORS.length]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Prices Tab */}
            <TabsContent value="prices" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Analiza Preturi din Facturi</CardTitle>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const res = await api.get('/reports/advanced-consumption/price-analysis');
                        setPriceAnalysis(res.data);
                        toast.success('Analiza preturi incarcata');
                      } catch { toast.error('Eroare'); }
                    }}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Incarca
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!priceAnalysis ? (
                    <p className="text-slate-400 text-center py-12">Apasati "Incarca" pentru a vedea analiza preturilor</p>
                  ) : (
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="border-b">
                            <th className="text-left py-2 px-3">Articol</th>
                            <th className="text-right py-2 px-3">Pret Actual</th>
                            <th className="text-right py-2 px-3">Pret Mediu</th>
                            <th className="text-right py-2 px-3">Min</th>
                            <th className="text-right py-2 px-3">Max</th>
                            <th className="text-right py-2 px-3">Variatie</th>
                            <th className="text-right py-2 px-3">Inregistrari</th>
                          </tr>
                        </thead>
                        <tbody>
                          {priceAnalysis.analysis?.map((item, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="py-2 px-3">
                                <div className="font-medium">{item.article_name}</div>
                                <div className="text-xs text-slate-500">{item.article_code}</div>
                              </td>
                              <td className="py-2 px-3 text-right font-semibold">{item.current_price?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                              <td className="py-2 px-3 text-right">{item.avg_price?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                              <td className="py-2 px-3 text-right text-green-600">{item.min_price?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                              <td className="py-2 px-3 text-right text-red-600">{item.max_price?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                              <td className="py-2 px-3 text-right">
                                <Badge className={item.price_variance > 10 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}>
                                  {item.price_variance?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}
                                </Badge>
                              </td>
                              <td className="py-2 px-3 text-right">{item.price_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Filter Dialog */}
      <Dialog open={showAddFilter} onOpenChange={setShowAddFilter}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adauga Filtru</DialogTitle>
            <DialogDescription>Selectati campul si valoarea pentru filtrare</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Camp</Label>
              <Select value={newFilterField} onValueChange={(v) => { setNewFilterField(v); setNewFilterValue(''); }}>
                <SelectTrigger data-testid="filter-field-select">
                  <SelectValue placeholder="Selectati campul" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plant_id">Fabrica</SelectItem>
                  <SelectItem value="location_id">Locatie</SelectItem>
                  <SelectItem value="equipment_id">Echipament</SelectItem>
                  <SelectItem value="technician_id">Tehnician</SelectItem>
                  <SelectItem value="category_id">Categorie</SelectItem>
                  <SelectItem value="supplier_id">Furnizor</SelectItem>
                  <SelectItem value="article_id">Articol</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newFilterField && (
              <div>
                <Label>Valoare</Label>
                <Select value={newFilterValue} onValueChange={setNewFilterValue}>
                  <SelectTrigger data-testid="filter-value-select">
                    <SelectValue placeholder="Selectati valoarea" />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilterValueOptions(newFilterField).map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFilter(false)}>Anuleaza</Button>
            <Button onClick={addFilter} disabled={!newFilterField || !newFilterValue}>Adauga</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salveaza Template Raport</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Nume template</Label>
            <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="ex: Consum lunar per echipament" data-testid="template-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Anuleaza</Button>
            <Button onClick={saveTemplate} disabled={!templateName.trim()}>Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvancedConsumptionReportPage;
